import { DesignPatientInfo } from './submit-design/DesignPatientInfo';
import { DxDraftPanel } from './submit-design/DxDraftPanel';
import { WorkQueueNotesPanel } from './WorkQueueNotesPanel';
import { RefinementInstructionsPanel } from './RefinementInstructionsPanel';
import { ClarificationHistory } from './ClarificationHistory';
import { PatientConversationPanel } from './PatientConversationPanel';
import { NotesTabLabel } from './NotesTabLabel';
import { VirtualAppointmentsTabLabel } from './VirtualAppointmentsTabLabel';
import { CountTabLabel } from './CountTabLabel';
import {
    useRefinementDesignConversation,
    REFINEMENT_CONVERSATION_QUERY_PREFIX,
    REFINEMENT_REVISION_EVENT_TYPE,
    REFINEMENT_RESUBMISSION_EVENT_TYPES,
    REFINEMENT_LABEL_OVERRIDES
} from './useRefinementDesignConversation';
import { OverlayScrollArea } from '@odo/shared-ui';
import { FilesTabContent } from '../../patient-files/FilesTabContent';
import { AnalysisTab } from '../../patient-files/analysis/AnalysisTab';
import { MonitoringTabPanel } from '../../patient-files/monitoring/MonitoringTabPanel';
import { DmNotesLanding } from '../../patient-files/actions/dm/DmNotesLanding';
import { DmPatientMessagesTab } from '../../patient-files/actions/dm/DmPatientMessagesTab';
import { VirtualAppointmentsReadOnlyPanel } from '../../virtual-appointments/VirtualAppointmentsReadOnlyPanel';
import {
    PatientNotificationsTab,
    PatientNotificationsTabLabel
} from '../../monitoring/notifications/view/PatientNotificationsTab';
import { CaseActionsTab } from '../../monitoring/shared/CaseActionsTab';
import { useDmMessagePatient } from '../../../hooks/useDmMessagesInbox';
import { PATIENT_LOOKUP_FILTERS } from '../../monitoring/messages/useDmMessagePanelPatient';
import { DxTabLabel } from './DxTabLabel';

interface SidebarTab {
    key: string;
    label: React.ReactNode;
    content: React.ReactNode;
}

function patientInfoTab(patientId: string): SidebarTab {
    return {
        key: 'info',
        label: 'Patient info',
        content: <DesignPatientInfo patientId={patientId} embedded />
    };
}

function patientNotesTab(patientId: string): SidebarTab {
    return {
        key: 'notes',
        label: <NotesTabLabel patientId={patientId} />,
        content: <WorkQueueNotesPanel patientId={patientId} />
    };
}

function refinementInstructionsTab(patientId: string): SidebarTab {
    return {
        key: 'refinement',
        label: 'Refinement instructions',
        content: <RefinementInstructionsPanel patientId={patientId} />
    };
}

function revisionsTab(patientId: string, headerAction?: React.ReactNode): SidebarTab {
    return {
        key: 'revisions',
        label: 'Revisions',
        content: (
            <ClarificationHistory
                patientId={patientId}
                queryKeyPrefix="redesign-conversation"
                config={{ revisionEventType: 'request-revision' }}
                embedded
                headerAction={headerAction}
            />
        )
    };
}

// "Revisions" tab label counting the Ref-6.1 <-> Ref-3 loop; reuses the panel's query.
// eslint-disable-next-line react-refresh/only-export-components
function RefinementRevisionsTabLabel({
    patientId,
    since
}: {
    patientId: string;
    since: string | null;
}) {
    const { revisionCount } = useRefinementDesignConversation(patientId, since);
    return (
        <span className="inline-flex items-center gap-1.5">
            Revisions
            {revisionCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-gray-200 text-gray-700 text-[10px] font-bold leading-none">
                    {revisionCount}
                </span>
            )}
        </span>
    );
}

function refinementRevisionsTab(
    patientId: string,
    since: string | null,
    headerAction?: React.ReactNode
): SidebarTab {
    return {
        key: 'refinement-revisions',
        label: <RefinementRevisionsTabLabel patientId={patientId} since={since} />,
        content: (
            <ClarificationHistory
                patientId={patientId}
                queryKeyPrefix={REFINEMENT_CONVERSATION_QUERY_PREFIX}
                config={{
                    revisionEventType: REFINEMENT_REVISION_EVENT_TYPE,
                    resubmissionEventTypes: REFINEMENT_RESUBMISSION_EVENT_TYPES,
                    since
                }}
                labelOverrides={REFINEMENT_LABEL_OVERRIDES}
                numberKind="revision"
                embedded
                headerAction={headerAction}
            />
        )
    };
}

function dxReportTab(patientId: string): SidebarTab {
    return {
        key: 'dx-report',
        label: <DxTabLabel patientId={patientId} />,
        content: <DxDraftPanel patientId={patientId} embedded />
    };
}

function patientFilesTab(patientId: string): SidebarTab {
    return {
        key: 'files',
        label: 'Patient files',
        content: <FilesTabContent patientId={patientId} />
    };
}

// Situational analysis, slim layout - the sidebar is far narrower than the files tab.
function analysisTab(patientId: string): SidebarTab {
    return {
        key: 'analysis',
        label: 'Analysis',
        content: (
            <OverlayScrollArea
                className="flex-1 min-h-0"
                innerClassName="min-h-full flex flex-col"
                trackInsetPx={6}
            >
                <AnalysisTab patientId={patientId} variant="slim" />
            </OverlayScrollArea>
        )
    };
}

function monitoringTab(patientId: string, canManageMonitoring: boolean): SidebarTab {
    return {
        key: 'monitoring',
        label: 'Monitoring',
        content: (
            <MonitoringTabPanel patientId={patientId} canManageMonitoring={canManageMonitoring} />
        )
    };
}

function conversationsTab(patientId: string): SidebarTab {
    return {
        key: 'conversations',
        label: 'Conversations',
        content: <PatientConversationPanel patientId={patientId} />
    };
}

// The DM landings run embedded: no back header, since the tab is the whole surface.
function dmNotesTab(patientId: string): SidebarTab {
    return {
        key: 'dm-notes',
        label: 'DM notes',
        content: <DmNotesLanding patientId={patientId} embedded />
    };
}

// "Patient messages" tab label, with the same unread badge as the 'dm-notifications' tab's.
// eslint-disable-next-line react-refresh/only-export-components
function PatientMessagesTabLabel({ patientId }: { patientId: string }) {
    const { data } = useDmMessagePatient(patientId, PATIENT_LOOKUP_FILTERS);
    return <CountTabLabel label="Patient messages" count={data?.patient.unreviewedCount ?? null} />;
}

function patientMessagesTab(patientId: string): SidebarTab {
    return {
        key: 'patient-messages',
        label: <PatientMessagesTabLabel patientId={patientId} />,
        content: <DmPatientMessagesTab patientId={patientId} embedded />
    };
}

function virtualAppointmentsTab(patientId: string, canAddVas: boolean): SidebarTab {
    return {
        key: 'vas',
        label: <VirtualAppointmentsTabLabel patientId={patientId} />,
        content: <VirtualAppointmentsReadOnlyPanel patientId={patientId} canAddVas={canAddVas} />
    };
}

interface DmNotificationsTabContext {
    onNextPatient?: () => void;
    onBackToList?: () => void;
}

export interface TabContext {
    /** Extra affordance for the Revisions tab header (e.g. "Request clarification"). */
    revisionsHeaderAction?: React.ReactNode;
    /** Where the DM notifications tab's all-reviewed state sends the user; omit for no actions. */
    dmNotifications?: DmNotificationsTabContext;
    /** The host renders DmControlRoute, so the Monitoring tab can open it. */
    manageMonitoring?: boolean;
    /** The VAs tab shows "Add VAs"; otherwise it stays read-only. */
    addVirtualAppointments?: boolean;
}

function dmNotificationsTab(patientId: string, ctx: DmNotificationsTabContext): SidebarTab {
    return {
        key: 'dm-notifications',
        label: <PatientNotificationsTabLabel patientId={patientId} />,
        content: (
            <PatientNotificationsTab
                patientId={patientId}
                onNextPatient={ctx.onNextPatient}
                onBackToList={ctx.onBackToList}
            />
        )
    };
}

function caseActionsTab(patientId: string): SidebarTab {
    return {
        key: 'actions',
        label: 'Actions',
        content: <CaseActionsTab patientId={patientId} />
    };
}

const TAB_FACTORIES: Record<
    SidebarTabKey,
    (patientId: string, since: string | null, ctx: TabContext) => SidebarTab
> = {
    info: (patientId) => patientInfoTab(patientId),
    notes: (patientId) => patientNotesTab(patientId),
    files: (patientId) => patientFilesTab(patientId),
    revisions: (patientId, _since, ctx) => revisionsTab(patientId, ctx.revisionsHeaderAction),
    refinement: (patientId) => refinementInstructionsTab(patientId),
    'refinement-revisions': (patientId, since, ctx) =>
        refinementRevisionsTab(patientId, since, ctx.revisionsHeaderAction),
    'dx-report': (patientId) => dxReportTab(patientId),
    analysis: (patientId) => analysisTab(patientId),
    monitoring: (patientId, _since, ctx) => monitoringTab(patientId, ctx.manageMonitoring ?? false),
    conversations: (patientId) => conversationsTab(patientId),
    'dm-notes': (patientId) => dmNotesTab(patientId),
    'patient-messages': (patientId) => patientMessagesTab(patientId),
    vas: (patientId, _since, ctx) =>
        virtualAppointmentsTab(patientId, ctx.addVirtualAppointments ?? false),
    'dm-notifications': (patientId, _since, ctx) =>
        dmNotificationsTab(patientId, ctx.dmNotifications ?? {}),
    actions: (patientId) => caseActionsTab(patientId)
};

export type SidebarTabKey =
    | 'info'
    | 'notes'
    | 'files'
    | 'revisions'
    | 'refinement'
    | 'refinement-revisions'
    | 'dx-report'
    | 'analysis'
    | 'monitoring'
    | 'conversations'
    | 'dm-notes'
    | 'patient-messages'
    | 'vas'
    | 'dm-notifications'
    | 'actions';

// Single dispatch point: task surfaces pick a named set, so adding a tab
// everywhere is a one-line change here instead of edits across every task.
export const DESIGN_TABS: SidebarTabKey[] = ['info', 'notes', 'files', 'dx-report', 'analysis'];
export const REDESIGN_TABS: SidebarTabKey[] = ['info', 'notes', 'files', 'revisions', 'analysis'];
export const OPS_TABS: SidebarTabKey[] = ['info', 'notes', 'files', 'analysis'];

// Info tabs beside the Design loop's Submissions and Dx report work tabs.
export function designLoopInfoTabs(macro: string): SidebarTabKey[] {
    return macro === 'redesign' ? REDESIGN_TABS : OPS_TABS;
}
export const REFINEMENT_TABS: SidebarTabKey[] = [
    'info',
    'refinement',
    'refinement-revisions',
    'notes',
    'analysis'
];

// Shared by the in-treatment workspaces: Manage Tx and Manage VAs.
export const IN_TREATMENT_TABS: SidebarTabKey[] = [
    'monitoring',
    'conversations',
    'dm-notes',
    'patient-messages',
    'notes',
    'files'
];

// "DM VA needed": the in-treatment set plus DM notifications, worked from the task.
export const DM_VA_TABS: SidebarTabKey[] = [
    'monitoring',
    'dm-notifications',
    'conversations',
    'dm-notes',
    'patient-messages',
    'notes',
    'files'
];

// Monitoring > Notifications patient view: the in-treatment set, plus VAs and patient info.
export const DM_NOTIFICATION_TABS: SidebarTabKey[] = [
    'monitoring',
    'vas',
    'info',
    'conversations',
    'patient-messages',
    'dm-notes',
    'notes',
    'files',
    'actions'
];

// Monitoring > Messages patient view: the chat is its left column, so no messages tab here.
export const DM_MESSAGE_TABS: SidebarTabKey[] = [
    'dm-notifications',
    'monitoring',
    'vas',
    'info',
    'conversations',
    'dm-notes',
    'notes',
    'files',
    'actions'
];

export function buildSidebarTabs(
    patientId: string,
    keys: SidebarTabKey[],
    since: string | null = null,
    ctx: TabContext = {}
): SidebarTab[] {
    return keys.map((key) => TAB_FACTORIES[key](patientId, since, ctx));
}
